import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import AppContext from "../../../../context/AppContext";
import {
  Container, Row, Col, Card, Table, Badge,
  Button, Modal, Form, InputGroup
} from 'react-bootstrap';
import {
  Award, ChevronLeft, ChevronRight,
  Search, Filter, X, Plus, Eye
} from 'lucide-react';
import '../../../../assets/css/admin/dashboardCSS/DonorLevels.css';
import AdminLayout from '../../../../layouts/admin/Adminlayout';

const ITEMS_PER_PAGE = 4;

const DonorLevels = () => {
  const { baseUrl, regExpChecker } = useContext(AppContext);

  // ── Form States ──
  const [levelName, setLevelName] = useState('');
  const [minimumAmount, setMinimumAmount] = useState('');
  const [maximumAmount, setMaximumAmount] = useState('');
  const [badgeimage, setBadgeimage] = useState('');
  const [rewardtitle, setRewardtitle] = useState('');
  const [description, setDescription] = useState('');
  const [rewardAmount, setRewardAmount] = useState('');
  const [status, setStatus] = useState('');

  // ── UI States ──
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [levels, setLevels] = useState([]);
  const [errors, setErrors] = useState({});


  const [levelNameError, setLevelNameError] = useState("");
  const [rewardTitleError, setRewardTitleError] = useState("");
  const [minimumAmountError, setMinimumAmountError] = useState("");
  const [rewardAmountError, setRewardAmountError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  // ── Filter & Paginate ──
  const filtered = (levels || []).filter(l => {
    const matchSearch =
      (l.levelName || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.rewardtitle || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || l.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const goToPage = (p) => setCurrentPage(Math.max(1, Math.min(p, totalPages)));
  const openDetail = (lvl) => { setSelected(lvl); setShowDetail(true); };

  // ── Reset Form ──
  const resetForm = () => {
    setLevelName('');
    setMinimumAmount('');
    setMaximumAmount('');
    setBadgeimage('');
    setRewardtitle('');
    setDescription('');
    setRewardAmount('');
    setStatus('');
    setErrors({});
  };

  // ── Validation ──
  const validate = () => {
    const newErrors = {};
    if (!levelName.trim()) newErrors.levelName = 'Level name is required';
    if (!minimumAmount) newErrors.minimumAmount = 'Minimum amount is required';
    if (!rewardtitle.trim()) newErrors.rewardtitle = 'Reward title is required';
    if (!rewardAmount) newErrors.rewardAmount = 'Reward amount is required';
    if (!status) newErrors.status = 'Status is required';
    return newErrors;
  };

  // ── Add New Level ──
  const AddNewLevel = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const formData = new FormData();
    formData.append("levelName", levelName);
    formData.append("minimumAmount", minimumAmount);
    formData.append("maximumAmount", maximumAmount);
    formData.append("badgeimage", badgeimage);
    formData.append("rewardtitle", rewardtitle);
    formData.append("rewardamount", rewardAmount);
    formData.append("description", description);
    formData.append("status", status);

    axios
      .post(`${baseUrl}/admin/DonorLevel/AddNewLevel`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        console.log(response.data);
        viewAllLevels();
        resetForm();
        setShowAddModal(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── View All Levels ──
  const viewAllLevels = () => {
    axios
      .get(`${baseUrl}/admin/DonorLevels/viewAllLevels`)
      .then((response) => {
        console.log("API RESPONSE:", response.data);
        setLevels(response.data.levels);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    viewAllLevels();
  }, []);

  return (
    <AdminLayout>
      <div className="sadl-wrapper">

        {/* ══ Page Header ══ */}
        <div className="sadl-page-header">
          <div className="sadl-header-content">
            <div className="sadl-header-text">
              <h1 className="sadl-title">Donor Levels &amp; Rewards</h1>
              <p className="sadl-subtitle">View donation tiers and reward details defined by the admin</p>
            </div>
            <Button className="sadl-add-btn" onClick={() => { resetForm(); setShowAddModal(true); }}>
              <Plus size={18} /> Add New Level
            </Button>
          </div>
        </div>

        <Container fluid className="sadl-content">

          {/* ══ Filter Bar ══ */}
          <Card className="sadl-filter-card mb-4">
            <Card.Body className="sadl-filter-body">
              <Row className="g-3 align-items-center">

                <Col xs={12} md={6}>
                  <InputGroup className="sadl-search-group">
                    <InputGroup.Text className="sadl-search-icon">
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search by name or reward..."
                      value={search}
                      onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                      className="sadl-search-input"
                    />
                    {search && (
                      <Button className="sadl-search-clear" onClick={() => setSearch('')}>
                        <X size={14} />
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                <Col xs={12} md={4}>
                  <InputGroup>
                    <InputGroup.Text className="sadl-filter-icon">
                      <Filter size={16} />
                    </InputGroup.Text>
                    <Form.Select
                      value={statusFilter}
                      onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                      className="sadl-filter-select"
                    >
                      <option value="All">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Form.Select>
                  </InputGroup>
                </Col>

                <Col xs={12} md={2} className="sadl-filter-count-col">
                  <span className="sadl-filter-count">
                    {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                  </span>
                </Col>

              </Row>
            </Card.Body>
          </Card>

          {/* ══ Table ══ */}
          <Card className="sadl-table-card mb-4">
            <Card.Body className="sadl-table-body">

              <div className="sadl-section-header">
                <h2 className="sadl-section-title">Levels Table</h2>
              </div>

              <div className="table-responsive">
                <Table className="sadl-table">
                  <thead>
                    <tr>
                      <th>Badge</th>
                      <th>Level</th>
                      <th>Donation Range</th>
                      <th>Reward Title</th>
                      <th>Reward (PKR)</th>
                      <th>Donors</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="sadl-empty-row">
                          <div className="sadl-empty-state">
                            <Award size={36} />
                            <p>No levels found</p>
                          </div>
                        </td>
                      </tr>
                    ) : paginated.map(lvl => (
                      <tr key={lvl.id} className="sadl-table-row">

                        <td>
                          <div
                            className="sadl-badge-icon"
                            style={{ background: `${lvl.color}18`, border: `2px solid ${lvl.color}40` }}
                          >
                            <span style={{ fontSize: 20 }}>{lvl.badgeimage}</span>
                          </div>
                        </td>

                        <td>
                          <span className="sadl-level-name" style={{ color: lvl.color }}>
                            {lvl.levelName}
                          </span>
                        </td>

                        <td>
                          <span className="sadl-range-chip">
                            {lvl.minimumAmount}
                            {lvl.maximumAmount ? ` – ${lvl.maximumAmount}` : '+'} donations
                          </span>
                        </td>

                        <td className="sadl-reward-title">{lvl.rewardtitle}</td>

                        <td>
                          <span className="sadl-reward-pill">
                            PKR {lvl.rewardamount ? Number(lvl.rewardamount).toLocaleString() : 0}
                          </span>
                        </td>

                        <td>
                          <Badge className="sadl-donor-badge">{lvl.donorCount}</Badge>
                        </td>

                        <td>
                          <span className={`sadl-status-pill ${lvl.status === 'Active' ? 'sadl-active' : 'sadl-inactive'}`}>
                            <span className="sadl-status-dot" />
                            {lvl.status}
                          </span>
                        </td>

                        <td>
                          <Button className="sadl-view-btn" size="sm" onClick={() => openDetail(lvl)}>
                            <Eye size={16} />
                          </Button>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* ── Pagination ── */}
              <Row className="sadl-pagination-row align-items-center">
                <Col xs={12} md={6} className="sadl-page-info">
                  Showing{' '}
                  {filtered.length === 0 ? 0 : (safePage - 1) * ITEMS_PER_PAGE + 1}–
                  {Math.min(safePage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} entries
                </Col>
                <Col xs={12} md={6}>
                  <div className="sadl-pagination">
                    <button className="sadl-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1}>
                      <ChevronLeft size={15} />
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        className={`sadl-page-btn ${safePage === i + 1 ? 'sadl-page-active' : ''}`}
                        onClick={() => goToPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button className="sadl-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages}>
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </Col>
              </Row>

            </Card.Body>
          </Card>

          {/* ══ Donor Progression Flow ══ */}
          <Card className="sadl-flow-card">
            <Card.Body>
              <div className="sadl-section-header">
                <h2 className="sadl-section-title">Donor Progression Flow</h2>
              </div>
              <div className="sadl-flow">
                {levels.map((lvl, i) => (
                  <React.Fragment key={lvl.id}>
                    <div
                      className={`sadl-flow-item ${lvl.status !== 'Active' ? 'sadl-flow-inactive' : ''}`}
                      style={{ '--flow-color': lvl.color }}
                    >
                      <span className="sadl-flow-icon">{lvl.badgeimage}</span>
                      <div className="sadl-flow-text">
                        <span className="sadl-flow-name">{lvl.levelName}</span>
                      </div>
                    </div>
                    {i < levels.length - 1 && (
                      <div className="sadl-flow-arrow">
                        <ChevronRight size={20} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Card.Body>
          </Card>

        </Container>

        {/* ══ Detail Modal ══ */}
        <Modal show={showDetail} onHide={() => setShowDetail(false)} centered size="md">
          <Modal.Header closeButton className="sadl-modal-header">
            <Modal.Title className="sadl-modal-title">Level Details</Modal.Title>
          </Modal.Header>
          <Modal.Body className="sadl-modal-body">
            {selected && (
              <div>
                <p><strong>Level Name:</strong> {selected.levelName}</p>
                <p><strong>Badge:</strong> {selected.badgeimage}</p>
                <p><strong>Min Amount:</strong> {selected.minimumAmount}</p>
                <p><strong>Max Amount:</strong> {selected.maximumAmount || 'Unlimited'}</p>
                <p><strong>Reward Title:</strong> {selected.rewardtitle}</p>
                <p><strong>Reward Amount:</strong> PKR {selected.rewardamount ? Number(selected.rewardamount).toLocaleString() : 0}</p>
                <p><strong>Description:</strong> {selected.description}</p>
                <p><strong>Status:</strong> {selected.status}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetail(false)}>Close</Button>
          </Modal.Footer>
        </Modal>

        {/* ══ Add New Level Modal ══ */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
          <Modal.Header closeButton className="sadl-modal-header">
            <Modal.Title className="sadl-modal-title">Add New Level</Modal.Title>
          </Modal.Header>
          <Modal.Body className="sadl-modal-body">
            <Row className="g-3">

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">Level Name</Form.Label>
                  <Form.Control
                    className="sadl-form-input"
                    placeholder="e.g. Bronze"
                    value={levelName}
                    onChange={(e) => {
                      setLevelName(e.target.value);
                      setLevelNameError(regExpChecker("string", e.target.value));
                    }}

                  />
                  <p style={{ color: "red", fontSize: "12px" }}>{levelNameError}</p>
                  {errors.levelName && <p style={{ color: "red", fontSize: "12px" }}>{errors.levelName}</p>}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">Icon (emoji)</Form.Label>
                  <Form.Control
                    className="sadl-form-input"
                    placeholder="e.g. 🥉"
                    value={badgeimage}
                    onChange={e => setBadgeimage(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">Min Donations</Form.Label>
                  <Form.Control
                    className="sadl-form-input"
                    type="number"
                    min="1"
                    placeholder="e.g. 1"
                    value={minimumAmount}
                    onChange={(e) => {
                      setMinimumAmount(e.target.value);
                      setMinimumAmountError(
                        e.target.value ? "" : "Minimum amount is required"
                      );
                    }}
                  />
                  <p style={{ color: "red", fontSize: "12px" }}>{minimumAmountError}</p>
                  {errors.minimumAmount && <p style={{ color: "red", fontSize: "12px" }}>{errors.minimumAmount}</p>}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">
                    Max Donations <span className="sadl-optional">(blank = unlimited)</span>
                  </Form.Label>
                  <Form.Control
                    className="sadl-form-input"
                    type="number"
                    min="1"
                    placeholder="e.g. 10"
                    value={maximumAmount}
                    onChange={e => setMaximumAmount(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">Reward Title</Form.Label>
                  <Form.Control
                    className="sadl-form-input"
                    placeholder="e.g. Bronze Badge"
                    value={rewardtitle}
                    onChange={(e) => {
                      setRewardtitle(e.target.value);
                      setRewardTitleError(regExpChecker("string", e.target.value));
                    }}
                  />
                  <p style={{ color: "red", fontSize: "12px" }}>{rewardTitleError}</p>
                  {errors.rewardtitle && <p style={{ color: "red", fontSize: "12px" }}>{errors.rewardtitle}</p>}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">Reward Amount (PKR)</Form.Label>
                  <Form.Control
                    className="sadl-form-input"
                    type="number"
                    min="0"
                    placeholder="e.g. 500"
                    value={rewardAmount}
                    onChange={(e) => {
                      setRewardAmount(e.target.value);
                      setRewardAmountError(
                        e.target.value ? "" : "Reward amount is required"
                      );
                    }}
                  />
                  <p style={{ color: "red", fontSize: "12px" }}>{rewardAmountError}</p>
                  {errors.rewardAmount && <p style={{ color: "red", fontSize: "12px" }}>{errors.rewardAmount}</p>}
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    className="sadl-form-input"
                    placeholder="Describe this level and its rewards..."
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setDescriptionError(regExpChecker("description", e.target.value));
                    }}
                  />
                  <p style={{ color: "red", fontSize: "12px" }}>{descriptionError}</p>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="sadl-form-label">Status</Form.Label>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      label="Active"
                      name="status"
                      id="s-active"
                      checked={status === 'Active'}
                      onChange={() => setStatus('Active')}
                    />
                    <Form.Check
                      type="radio"
                      label="Inactive"
                      name="status"
                      id="s-inactive"
                      checked={status === 'Inactive'}
                      onChange={() => setStatus('Inactive')}
                    />
                  </div>
                  {errors.status && <p style={{ color: "red", fontSize: "12px" }}>{errors.status}</p>}
                </Form.Group>
              </Col>

            </Row>

            <div className="sadl-modal-footer mt-3">
              <Button variant="light" className="sadl-cancel-btn" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button className="sadl-save-btn" onClick={AddNewLevel}>
                Save Level
              </Button>
            </div>
          </Modal.Body>
        </Modal>

      </div>
    </AdminLayout>
  );
};

export default DonorLevels;